import { createApi } from "@reduxjs/toolkit/query/react";
import { adminBaseQuery } from "@store/baseQuery";

// Type definitions based on swagger schema
export interface PaymentProvider {
  id: number;
  name: string;
  display_name: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePaymentProviderRequest {
  name: string;
  display_name: string;
}

export interface UpdatePaymentProviderRequest {
  name?: string;
  display_name?: string;
}

export const paymentProvidersApi = createApi({
  reducerPath: "paymentProvidersApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["PaymentProvider"],
  endpoints: (builder) => ({
    // Get all payment providers
    getPaymentProviders: builder.query<PaymentProvider[], void>({
      query: () => "/payment-providers",
      providesTags: ["PaymentProvider"],
    }),

    // Get a specific payment provider
    getPaymentProvider: builder.query<PaymentProvider, number>({
      query: (id) => `/payment-providers/${id}`,
      providesTags: (result, error, id) => [
        { type: "PaymentProvider", id },
      ],
    }),

    // Create a new payment provider
    createPaymentProvider: builder.mutation<
      PaymentProvider,
      CreatePaymentProviderRequest
    >({
      query: (data) => ({
        url: "/payment-providers",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PaymentProvider"],
    }),

    // Update a specific payment provider
    updatePaymentProvider: builder.mutation<
      PaymentProvider,
      { id: number; data: UpdatePaymentProviderRequest }
    >({
      query: ({ id, data }) => ({
        url: `/payment-providers/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "PaymentProvider", id },
        "PaymentProvider",
      ],
    }),

    // Delete a specific payment provider
    deletePaymentProvider: builder.mutation<
      { payment_provider_id: number },
      number
    >({
      query: (id) => ({
        url: `/payment-providers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PaymentProvider"],
    }),
  }),
});

export const {
  useGetPaymentProvidersQuery,
  useGetPaymentProviderQuery,
  useCreatePaymentProviderMutation,
  useUpdatePaymentProviderMutation,
  useDeletePaymentProviderMutation,
} = paymentProvidersApi; 